/**
 * useFormSchema — hook que carrega FormSchema do backend (A6.6).
 *
 * GET /api/v1/forms/schema?entidade=X&entidadeId=Y&scope_tipo=Z&scope_id=W
 *
 * Retorna campos custom aplicáveis ao contexto + valores atuais (se
 * entidadeId fornecido) + obrigatoriedade resolvida.
 *
 * NÃO REVERTER:
 *   - Cache key inclui contexto completo pra invalidação granular
 *   - staleTime 30s — frontend cacheia mas refresh frequente OK
 *   - enabled depende de entidade não vazia (evita request inválida)
 */
import { useQuery } from '@tanstack/react-query';

import { api } from '@/lib/api';

export type ScopeTipo = 'global' | 'workflow' | 'modal' | 'pagina';
export type FieldTipo =
  | 'text' | 'textarea' | 'number' | 'currency' | 'percentage'
  | 'email' | 'phone' | 'cpf' | 'cnpj' | 'date' | 'datetime' | 'time'
  | 'dropdown' | 'multi_select' | 'checkbox' | 'radio'
  | 'file' | 'image' | 'url' | 'computed' | 'json';

export interface FormFieldSchema {
  id: string;
  key: string;
  label: string;
  descricao: string | null;
  tipo: FieldTipo;
  opcoes: Array<{ value: string; label: string }>;
  validacao: Record<string, unknown> | null;
  computedExpression: string | null;
  defaultValue: unknown;
  placeholder: string | null;
  prefix: string | null;
  suffix: string | null;
  largura: 'full' | 'half' | 'third' | 'quarter';
  ordem: number;
  grupo: string | null;
  icone: string | null;
  obrigatorio: boolean;
  obrigatoriedadeMensagem: string | null;
  editable: boolean;
  valor: unknown;
}

export interface FormSchema {
  entidade: string;
  entidadeId: string | null;
  scopeTipo: ScopeTipo | null;
  scopeId: string | null;
  campos: FormFieldSchema[];
  generatedAtIso: string;
}

export interface UseFormSchemaParams {
  entidade: string;
  entidadeId?: string | null;
  scopeTipo?: ScopeTipo;
  scopeId?: string;
  /**
   * `false` desliga a busca (o schema vem de fora). Existe pro formulário
   * PÚBLICO de captação: lá não há sessão, e `GET /api/v1/forms/schema` é
   * `requireAuth` — se a query rodar, o visitante toma 401 e o formulário
   * não abre. Default `true` (todas as telas internas).
   */
  enabled?: boolean;
}

export function useFormSchema(params: UseFormSchemaParams) {
  const { entidade, entidadeId, scopeTipo, scopeId, enabled } = params;
  return useQuery({
    queryKey: ['form-schema', entidade, entidadeId, scopeTipo, scopeId],
    queryFn: async () => {
      const qs = new URLSearchParams();
      qs.set('entidade', entidade);
      if (entidadeId) qs.set('entidadeId', entidadeId);
      if (scopeTipo) qs.set('scope_tipo', scopeTipo);
      if (scopeId) qs.set('scope_id', scopeId);
      return await api.get<FormSchema>(`/api/v1/forms/schema?${qs}`);
    },
    enabled: enabled !== false && !!entidade,
    staleTime: 30_000,
  });
}

/**
 * submitForm — POST /api/v1/forms/submit
 * Valida + persiste valores. 422 com lista detalhada de erros se algum
 * campo falhar (all-or-nothing).
 */
export interface SubmitFormPayload {
  entidade: string;
  entidadeId: string;
  scopeTipo?: ScopeTipo;
  scopeId?: string | null;
  valores: Record<string, unknown>;
  dados?: Record<string, unknown>;
}

export interface SubmitFormError {
  customFieldId: string;
  key: string;
  mensagem: string;
}

export interface SubmitFormResponse {
  ok?: true;
  persistidos?: number;
  error?: string;
  tag?: string;
  violacoes?: SubmitFormError[];
}

export async function submitForm(payload: SubmitFormPayload): Promise<SubmitFormResponse> {
  return await api.post<SubmitFormResponse>('/api/v1/forms/submit', payload);
}
